
import React from 'react';
import { useTranslationUtils } from '@/hooks/useTranslationUtils';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { Loader2, Image } from 'lucide-react';
import { UploadDropzoneProps } from './types';

const UploadDropzone: React.FC<UploadDropzoneProps> = ({
  onFileSelect,
  accept,
  uploading,
  error,
  placeholderText,
  buttonLabel,
  disabled = false
}) => {
  const { t } = useTranslationUtils();
  
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      onFileSelect(event.target.files[0]);
    }
  };
  
  return (
    <div className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-md p-6 bg-gray-50">
      <Image className="h-8 w-8 text-gray-400 mb-2" />
      <p className="text-sm text-gray-500 mb-2">
        {placeholderText || t('admin.dragAndDrop', 'Drag and drop or click to upload')}
      </p>
      <Label 
        htmlFor="image-upload" 
        className={cn(
          "cursor-pointer bg-primary text-primary-foreground hover:bg-primary/90 px-4 py-2 rounded-md text-sm",
          disabled && "opacity-50 cursor-not-allowed"
        )}
      >
        {uploading ? (
          <div className="flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            {t('admin.uploading', 'Uploading...')}
          </div>
        ) : (
          buttonLabel || t('admin.selectFile', 'Select File')
        )}
      </Label>
      <Input
        id="image-upload"
        type="file"
        accept={accept}
        onChange={handleFileChange}
        disabled={uploading || disabled}
        className="hidden"
      />
      {error && (
        <p className="text-sm text-red-500 mt-2">{error}</p>
      )}
    </div>
  );
};

export default UploadDropzone;
